// Builds FAQPage JSON-LD from the same array a FAQ component renders.
//
// The homepage previously advertised "10 questions" while its hand-authored
// CMS FAQPage block listed 3 — two unlinked sources of the same content that
// had drifted. Deriving the schema from the rendered array is what stops that
// happening again: if the visible list changes, the markup changes with it.
//
// Google retired FAQ rich results on 2026-05-07, so this earns no SERP
// feature. It is here because FAQ-style Q&A is the format AI search engines
// most readily cite, and because structured data that contradicts the visible
// page is a quality signal worth not sending.

const NAMED_ENTITIES = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  ndash: "–",
  mdash: "—",
  hellip: "…",
};

// CMS answers are HTML. `acceptedAnswer.text` should be readable prose, so
// tags come out and entities are decoded — including the double-encoded
// `&amp;nbsp;` that CMS copy tends to carry, which would otherwise surface
// literally in the markup.
const htmlToText = (html) => {
  if (typeof html !== "string" || !html) return "";

  let text = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
    .replace(/<[^>]*>/g, " ");

  // Two passes so `&amp;nbsp;` resolves to a space rather than to "&nbsp;".
  for (let pass = 0; pass < 2; pass += 1) {
    text = text
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) =>
        String.fromCharCode(parseInt(code, 16)),
      )
      .replace(/&([a-z]+);/gi, (match, name) => {
        const key = name.toLowerCase();
        return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key)
          ? NAMED_ENTITIES[key]
          : match;
      });
  }

  return text.replace(/\s+/g, " ").trim();
};

/**
 * @param {Array<{question: string, answer: string}>} faqs
 * @returns {object|null} FAQPage schema, or null when nothing is answerable.
 */
export const buildFaqPageSchema = (faqs) => {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;

  const mainEntity = faqs
    .map((faq) => {
      const name = htmlToText(faq?.question);
      const text = htmlToText(faq?.answer);
      if (!name || !text) return null;
      return {
        "@type": "Question",
        name,
        acceptedAnswer: { "@type": "Answer", text },
      };
    })
    .filter(Boolean);

  if (mainEntity.length === 0) return null;

  return { "@type": "FAQPage", mainEntity };
};

export { htmlToText };

// Section types that render a FAQ component, and therefore now emit their own
// complete FAQPage derived from the rendered questions.
const FAQ_SECTION_TYPES = new Set([
  "faqsection",
  "faq",
  "faqs",
  "faq_section",
  "eventfaq",
]);

export const hasFaqSection = (sections) =>
  Array.isArray(sections) &&
  sections.some((s) =>
    FAQ_SECTION_TYPES.has(String(s?.type ?? s?.component ?? "").toLowerCase()),
  );

const isFaqPageNode = (node) => {
  const type = node?.["@type"];
  const types = Array.isArray(type) ? type : [type];
  return types.includes("FAQPage");
};

/**
 * Removes FAQPage nodes from a CMS-authored schema.
 *
 * Called only for pages that render a FAQ component. Those components build
 * their FAQPage from the questions actually on the page, so leaving the
 * hand-authored CMS copy in place would publish two FAQPage nodes — and on the
 * homepage they disagreed, 3 questions against the visible 10.
 *
 * Returns null when nothing but FAQPage content was left, so the caller
 * renders no script at all.
 */
export const stripFaqPageNodes = (schema) => {
  if (!schema || typeof schema !== "object") return schema;

  if (Array.isArray(schema)) {
    const kept = schema.map(stripFaqPageNodes).filter(Boolean);
    return kept.length ? kept : null;
  }

  if (Array.isArray(schema["@graph"])) {
    const graph = schema["@graph"].filter((node) => !isFaqPageNode(node));
    if (graph.length === 0) return null;
    return { ...schema, "@graph": graph };
  }

  return isFaqPageNode(schema) ? null : schema;
};
